
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

interface Material {
  id: string;
  name: string;
  type: string;
  percentage?: number;
}

interface FormulaReviewStepProps {
  formData: {
    name: string;
    description: string;
    materials: Material[];
  };
}

export const FormulaReviewStep = ({ formData }: FormulaReviewStepProps) => {
  const rawMaterials = formData.materials.filter(m => m.type === 'raw');
  const chemicals = formData.materials.filter(m => m.type === 'chemical');
  
  const totalPercentage = formData.materials.reduce((sum, material) => {
    return sum + (material.percentage || 0);
  }, 0);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Review Formula</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Please review the details of your formula before submitting.
        </p>
      </div>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">{formData.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">Description</h4>
              <p>{formData.description || 'No description provided'}</p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Composition</h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Percentage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.materials.map((material) => (
                      <TableRow key={material.id}>
                        <TableCell className="font-medium">{material.name}</TableCell>
                        <TableCell>
                          <Badge variant={material.type === 'raw' ? 'outline' : 'secondary'}>
                            {material.type === 'raw' ? 'Raw Material' : 'Chemical'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {material.percentage?.toFixed(2) || 0}%
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2} className="font-medium text-right">
                        Total
                      </TableCell>
                      <TableCell className={`text-right font-bold ${
                        Math.abs(totalPercentage - 100) < 0.01 
                          ? 'text-green-600' 
                          : 'text-amber-600'
                      }`}>
                        {totalPercentage.toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              
              {Math.abs(totalPercentage - 100) >= 0.01 && (
                <p className="text-amber-500 text-sm mt-2">
                  Warning: Formula composition does not equal 100%
                </p>
              )}
            </div>
            
            <div className="pt-3 border-t">
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Summary</h4>
              <ul className="text-sm space-y-1">
                <li>Total materials: {formData.materials.length}</li>
                <li>Raw materials: {rawMaterials.length}</li>
                <li>Chemicals: {chemicals.length}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
